import { Suspense, useEffect, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { BackSide, TextureLoader } from "three";
import { ErrorBoundary } from "react-error-boundary";

import TextInput from "../components/refresh/inputs/TextInput";
import Submit from "../components/refresh/inputs/Submit";
import Flash from "../components/Flash";
import * as session from "../services/session";
import { isLoggedIn as isLoggedInSelector } from "../services/session/selectors";
import Checkbox from "../components/refresh/inputs/Checkbox";

const useStyles = createUseStyles({
  background: {
    width: "100%",
    height: "100vh",
    display: "flex",
    "flex-direction": "column",
    "justify-content": "center",
    "align-items": "center",
    "background-color": "#333",

    "& h1, h2": {
      margin: 0,
    },
  },
  container: {
    "background-color": "#252525",
    color: "white",
    "border-radius": 5,
    display: "flex",
    "flex-direction": "column",
    padding: 20,
    width: "95%",
    "max-width": 300,
    margin: [10, 0],
    "z-index": 10,
  },
  form: {
    display: "flex",
    "flex-direction": "column",
    "& input": {
      fontSize: "16px !important",
    }
  },
  canvas: {
    position: "absolute",
    height: "100vh",
    width: "100%",
    top: 0,
    left: 0,
  },
});

const Photosphere = ({ url }) => {
  const texture = useLoader(TextureLoader, url);
  const ref = useRef();

  useFrame(() => {
    ref.current.rotation.y += 0.001;
  });

  return (
    <mesh ref={ref}>
      <sphereBufferGeometry args={[600, 100, 100]} />
      <meshBasicMaterial map={texture} side={BackSide} />
    </mesh>
  );
};

const Login = () => {
  const isLoggedIn = useSelector(isLoggedInSelector);
  const classes = useStyles();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const mounted = useRef(true);

  const attemptLogin = e => {
    e.preventDefault();

    setLoading(true);
    setError("");

    session
      .login({ email, password }, remember)
      .then(() => {
        if (mounted.current) {
          setLoading(false);
          setPassword("");
        }
      })
      .catch(err => {
        if (mounted.current) {
          setLoading(false);

          if (err.status === 401) {
            setError("Login details incorrect.");
          } else {
            setError("An error occured.");
          }
        }
      });
  };

  useEffect(() => () => (mounted.current = false), []);

  if (isLoggedIn) {
    return (
      <Redirect
        to={{
          pathname: "/",
        }}
      />
    );
  }

  const url = "https://imgix.imgo.theatro360.com/snow.jpg";
  const hdparams = "auto=format";
  const sdparams = "auto=format&q=50&blur=30&w=1024&h=512";

  return (
    <div className={classes.background}>
      <div className={classes.container}>
        <h1>TouchTour Studio</h1>
        <form className={classes.form} onSubmit={attemptLogin}>
          {error.length > 0 && <Flash type="error" message={error} />}
          <TextInput
            label="Email"
            placeholder="Email"
            value={email}
            onChange={email => setEmail(email)}
          />
          <TextInput
            label="Password"
            placeholder="Password"
            type="password"
            value={password}
            onChange={password => setPassword(password)}
          />
          <Checkbox value={remember} onChange={x => setRemember(x)}>
            Keep me logged in
          </Checkbox>
          <Submit value="Log in" color="#37bbc8" disabled={loading} />
        </form>
      </div>
      <div className={classes.container}>
        <h2>View a Tour</h2>
        <form className={classes.form} onSubmit={attemptLogin}>
          <TextInput
            label="Tour code"
            placeholder="XX-XXXX"
            value={token}
            onChange={chars =>
              setToken(
                chars
                  .toUpperCase()
                  .replace(/[^-A-Z0-9\p{Pd}]/g, "")
                  .slice(0, 7),
              )
            }
          />
          <Submit
            value="View Tour"
            disabled={!token.match(/[A-Z0-9]{2}-[A-Z0-9]{4}/)}
          />
        </form>
      </div>
      <div className={classes.canvas}>
        <ErrorBoundary fallback={<></>}>
          <Canvas className={classes.canvas}>
            <Suspense fallback={null}>
              <Suspense fallback={<Photosphere url={`${url}?${sdparams}`} />}>
                <Photosphere url={`${url}?${hdparams}`} />
              </Suspense>
            </Suspense>
          </Canvas>
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default Login;
